import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ForceGraph2D, { type ForceGraphMethods } from "react-force-graph-2d";
import type { GraphSeedResponse } from "@/hooks/useMemoryGraph";

// ─── Palette ──────────────────────────────────────────────────────────────────

export const NODE_COLORS: Record<string, string> = {
  component: "#3fb950",
  pattern: "#a371f7",
  session: "#d29922",
  calibration: "#f0883e",
  story: "#58a6ff",
  epic: "#1f6feb",
  term: "#8b949e",
  principle: "#f778ba",
  decision: "#db6d28",
  guardrail: "#f85149",
  skill: "#79c0ff",
  module: "#56d364",
  bounded_context: "#7ee787",
  requirement: "#d2a8ff",
  layer: "#388bfd",
  architecture: "#3fb950",
  outcome: "#a5d6ff",
  project: "#ffd33d",
  symbol: "#42a5f5",
  workitem: "#ff7043",
  // Extended types
  concept: "#b392f0",
  command: "#e36209",
  practice: "#2ea44f",
  workflow: "#0075ca",
  tool: "#6e7681",
  knowledge: "#f0e68c",
  evidence: "#85e89d",
  reference: "#c8e1ff",
};

export const NODE_VAL: Record<string, number> = {
  module: 8,
  component: 6,
  bounded_context: 7,
  layer: 5,
  architecture: 5,
  epic: 7,
  story: 4,
  pattern: 5,
  decision: 4,
  guardrail: 5,
  principle: 5,
  skill: 4,
  calibration: 3,
  session: 3,
  term: 3,
  requirement: 4,
  outcome: 4,
  project: 8,
  symbol: 2,
  workitem: 6,
};

const DEFAULT_COLOR = "#484f58";
const MAX_EDGES = 3000;
const MASSIVE_THRESHOLD = 3000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function getRGBA(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getGraphAwareNodeScale(totalNodes: number): number {
  const safe = Math.max(totalNodes, 1);
  return clamp(2.5 / (1 + Math.log10(safe) * 0.95), 0.45, 2.5);
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface FGNode {
  id: string;
  name: string;
  type: string;
  score: number;
  sourceFile: string;
  properties: Record<string, unknown>;
  degree: number;
  val: number;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface FGLink {
  source: string | FGNode;
  target: string | FGNode;
  type: string;
  weight: number;
}

// ─── Props (unchanged contract) ───────────────────────────────────────────────

interface MemoryGraphD3Props {
  graph: GraphSeedResponse;
  onNodeClick: (nodeId: string) => void;
  selectedNodeId: string | null;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MemoryGraphD3({
  graph,
  onNodeClick,
  selectedNodeId,
  className,
}: MemoryGraphD3Props) {
  const fgRef = useRef<ForceGraphMethods<FGNode, FGLink> | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  // Guard: auto-fit once per component mount (never reset — expand must not re-fit)
  const hasAutoFittedRef = useRef(false);
  // Position cache: carry x/y/vx/vy forward across graph updates to avoid layout reset.
  // The Map holds references to the FGNode objects that react-force-graph/d3 has mutated
  // with x/y/vx/vy, so subsequent renders can seed new nodes with previous positions.
  // Updated via useEffect after graphData is committed — never written during render.
  const prevNodesRef = useRef<Map<string, FGNode>>(new Map());
  // Snapshot of the cache read once at render time (before any closures capture it).
  // Reading a ref in the render body is allowed by React; we avoid reading inside
  // callbacks/useMemo to satisfy react-hooks/refs. This value is stable for the
  // duration of this render and feeds into the graphData useMemo via its dep array.
  const prevNodeSnapshot = prevNodesRef.current;

  // D2: inverted filter — tracks which types are HIDDEN (starts empty = all visible)
  const [excludedTypes, setExcludedTypes] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoverNode, setHoverNode] = useState<FGNode | null>(null);

  const nodeSize = 3.0;
  const lineWidth = 0.24;

  // ── Adjacency for focus mode ──

  const adjacencyMap = useMemo(() => {
    const adj = new Map<string, Set<string>>();
    graph.edges.forEach((e) => {
      if (!adj.has(e.source)) adj.set(e.source, new Set());
      if (!adj.has(e.target)) adj.set(e.target, new Set());
      adj.get(e.source)!.add(e.target);
      adj.get(e.target)!.add(e.source);
    });
    return adj;
  }, [graph.edges]);

  // ── Derived type counts (pure, no setState) ──

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const n of graph.nodes) {
      counts[n.node_type] = (counts[n.node_type] ?? 0) + 1;
    }
    return counts;
  }, [graph.nodes]);

  // ── Map GraphSeedResponse → fresh graphData (carries positions from cache) ──

  const graphData = useMemo(() => {
    if (graph.nodes.length === 0) {
      return { nodes: [] as FGNode[], links: [] as FGLink[] };
    }

    const degreeMap = new Map<string, number>();
    graph.edges.forEach((e) => {
      degreeMap.set(e.source, (degreeMap.get(e.source) ?? 0) + 1);
      degreeMap.set(e.target, (degreeMap.get(e.target) ?? 0) + 1);
    });

    const nodeIds = new Set(graph.nodes.map((n) => n.node_id));

    // prevNodeSnapshot is a Map captured from prevNodesRef.current in the render body
    // (before this useMemo). The react-hooks/refs rule flags any closure that captures
    // a ref-derived value; we suppress it here because prevNodeSnapshot is a plain Map
    // used as a read-only position cache — it does not trigger re-renders and is safe
    // under StrictMode double-invocation (idempotent: same positions written twice).
    // eslint-disable-next-line react-hooks/refs
    const nodes: FGNode[] = graph.nodes.map((n) => {
      const degree = degreeMap.get(n.node_id) ?? 0;
      const baseVal = NODE_VAL[n.node_type] ?? 3;
      // prevNodeSnapshot: captured before this useMemo, holding positions from the
      // last committed render (d3 has mutated x/y/vx/vy on those FGNode objects).
      const prev = prevNodeSnapshot.get(n.node_id);
      const fgNode: FGNode = {
        id: n.node_id,
        name: n.label,
        type: n.node_type,
        score: (n.properties.effective_score as number) ?? 0,
        sourceFile: n.source_file ?? "",
        properties: n.properties,
        degree,
        val: baseVal + Math.min(degree * 0.3, 5),
        ...(prev !== undefined && {
          x: prev.x,
          y: prev.y,
          vx: prev.vx,
          vy: prev.vy,
        }),
      };
      return fgNode;
    });

    // Edge sampling: important-first, cap related_to
    let rawEdges = graph.edges.filter(
      (e) => nodeIds.has(e.source) && nodeIds.has(e.target),
    );
    if (rawEdges.length > MAX_EDGES) {
      const important = rawEdges.filter((l) => l.edge_type !== "related_to");
      const related = rawEdges.filter((l) => l.edge_type === "related_to");
      const remaining = MAX_EDGES - important.length;
      rawEdges = [...important, ...related.slice(0, Math.max(0, remaining))];
    }

    const links: FGLink[] = rawEdges.map((e) => ({
      source: e.source,
      target: e.target,
      type: e.edge_type,
      weight: e.weight,
    }));

    return { nodes, links };
    // prevNodeSnapshot intentionally excluded from deps: it is a stable snapshot
    // of a ref captured before this render. Including it would cause an infinite
    // recalculation because useLayoutEffect writes the ref after graphData commits,
    // changing prevNodeSnapshot on the next render (which is the desired one-render
    // lag that seeds new nodes with positions from the completed simulation tick).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  // ── Update position cache after each graphData commit ──
  // Runs after graphData is committed, by which point react-force-graph has received the
  // new graphData prop. The FGNode objects in graphData.nodes are the same objects that d3
  // mutates with x/y/vx/vy, so storing the references captures live positions.

  useEffect(() => {
    if (graphData.nodes.length === 0) {
      prevNodesRef.current = new Map();
    } else {
      prevNodesRef.current = new Map(graphData.nodes.map((n) => [n.id, n]));
    }
  }, [graphData]);

  // ── Derived state ──

  const graphAwareNodeScale = useMemo(
    () => getGraphAwareNodeScale(graphData.nodes.length),
    [graphData.nodes.length],
  );

  const isMassive = graphData.nodes.length > MASSIVE_THRESHOLD;

  const visibleNodeIds = useMemo(() => {
    const ids = new Set<string>();
    const lowerSearch = searchTerm.toLowerCase();
    graphData.nodes.forEach((n) => {
      if (excludedTypes.has(n.type)) return;
      if (
        searchTerm &&
        !n.id.toLowerCase().includes(lowerSearch) &&
        !n.name.toLowerCase().includes(lowerSearch)
      )
        return;
      ids.add(n.id);
    });
    return ids;
  }, [graphData.nodes, excludedTypes, searchTerm]);

  const focusSet = useMemo(() => {
    if (!selectedNodeId) return null;
    const nodes = new Set<string>();
    nodes.add(selectedNodeId);
    adjacencyMap.get(selectedNodeId)?.forEach((nid) => nodes.add(nid));
    const links = new Set<string>();
    graphData.links.forEach((l) => {
      const src = typeof l.source === "object" ? l.source.id : l.source;
      const tgt = typeof l.target === "object" ? l.target.id : l.target;
      if (src === selectedNodeId || tgt === selectedNodeId) {
        links.add(`${src}-${tgt}`);
      }
    });
    return { nodes, links };
  }, [selectedNodeId, adjacencyMap, graphData.links]);

  // ── Resize observer ──

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // ── nodeCanvasObject — CGC classic pattern ──

  const nodeCanvasObject = useCallback(
    (node: FGNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
      if (!visibleNodeIds.has(node.id)) return;
      if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) return;

      const isHovered = hoverNode?.id === node.id;
      const isFocused = focusSet ? focusSet.nodes.has(node.id) : true;
      const baseColor = NODE_COLORS[node.type] || DEFAULT_COLOR;
      const radius = node.val * 0.8 * nodeSize * graphAwareNodeScale;
      const opacity = isFocused ? (isHovered ? 1 : 0.9) : 0.05;

      if (!Number.isFinite(radius)) return;

      // Massive mode: cheap fillRect for unfocused
      if (isMassive && !isFocused && !isHovered) {
        ctx.fillStyle = getRGBA(baseColor, opacity);
        ctx.fillRect(node.x! - radius, node.y! - radius, radius * 2, radius * 2);
        return;
      }

      // Hover/selection glow ring
      if (isHovered || (selectedNodeId && node.id === selectedNodeId)) {
        ctx.beginPath();
        ctx.arc(node.x!, node.y!, radius * (isHovered ? 2.5 : 2.0), 0, 2 * Math.PI, false);
        ctx.fillStyle = getRGBA(baseColor, isHovered ? (isFocused ? 0.3 : 0.1) : 0.15);
        ctx.fill();
      }

      // Node body
      ctx.beginPath();
      ctx.arc(node.x!, node.y!, radius, 0, 2 * Math.PI, false);
      ctx.fillStyle = isFocused ? baseColor : getRGBA(baseColor, opacity);
      ctx.fill();

      // Labels — show on hover, or at zoom > threshold
      const showLabel = isHovered || (isFocused && globalScale > (isMassive ? 5.0 : 2.0));

      if (showLabel) {
        const fontSize = Math.max(2, Math.round((isHovered ? 14 : 10) / globalScale));
        const labelY = node.y! + radius + fontSize / 2 + 2;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = isFocused ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.1)";
        ctx.font = `${isHovered ? "bold" : "normal"} ${fontSize}px Inter, sans-serif`;
        if (isFocused && !isMassive) {
          ctx.shadowColor = "black";
          ctx.shadowBlur = 4;
        }
        ctx.fillText(node.name || "Unknown", node.x!, labelY);
        if (isFocused && !isMassive) ctx.shadowBlur = 0;
      }
    },
    [visibleNodeIds, hoverNode, focusSet, selectedNodeId, graphAwareNodeScale, nodeSize, isMassive],
  );

  // ── Hit area ──

  const nodePointerAreaPaint = useCallback(
    (node: FGNode, color: string, ctx: CanvasRenderingContext2D) => {
      if (!visibleNodeIds.has(node.id)) return;
      if (node.x == null || node.y == null) return;
      const radius = node.val * 0.8 * nodeSize * graphAwareNodeScale;
      const hitSize = radius * 1.5;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(node.x, node.y, hitSize, 0, 2 * Math.PI, false);
      ctx.fill();
    },
    [visibleNodeIds, nodeSize, graphAwareNodeScale],
  );

  // ── Link styling ──

  const getLinkColor = useCallback(
    (link: FGLink) => {
      const src = typeof link.source === "object" ? link.source.id : link.source;
      const tgt = typeof link.target === "object" ? link.target.id : link.target;

      if (!visibleNodeIds.has(src) || !visibleNodeIds.has(tgt)) return "rgba(0,0,0,0)";

      if (focusSet) {
        const key = `${src}-${tgt}`;
        const keyRev = `${tgt}-${src}`;
        const isPath = focusSet.links.has(key) || focusSet.links.has(keyRev);
        return isPath ? "rgba(88,166,255,0.6)" : "rgba(255,255,255,0.02)";
      }

      return "rgba(255,255,255,0.08)";
    },
    [focusSet, visibleNodeIds],
  );

  const getLinkWidth = useCallback(
    (link: FGLink) => {
      if (!focusSet) return lineWidth;
      const src = typeof link.source === "object" ? link.source.id : link.source;
      const tgt = typeof link.target === "object" ? link.target.id : link.target;
      const key = `${src}-${tgt}`;
      const keyRev = `${tgt}-${src}`;
      return focusSet.links.has(key) || focusSet.links.has(keyRev) ? 1.5 : lineWidth;
    },
    [focusSet, lineWidth],
  );

  // ── Tooltip ──

  const getNodeLabel = useCallback(
    (node: FGNode) => {
      if (!visibleNodeIds.has(node.id)) return "";
      const color = NODE_COLORS[node.type] || DEFAULT_COLOR;
      const safeName = escapeHtml(node.name);
      const safeType = escapeHtml(node.type);
      const safeFile = node.sourceFile ? escapeHtml(node.sourceFile) : "";
      return `<div style="font-weight:600;color:${color};margin-bottom:2px">${safeName}</div>
<div style="font-size:12px;color:#8b949e">${safeType} · degree ${node.degree}</div>
<div style="font-size:12px;color:#c9d1d9">Score: ${node.score.toFixed(2)}</div>
${safeFile ? `<div style="font-size:11px;color:#d29922;margin-top:2px">${safeFile}</div>` : ""}`;
    },
    [visibleNodeIds],
  );

  // ── Visibility ──

  const nodeVisibility = useCallback(
    (node: FGNode) => visibleNodeIds.has(node.id),
    [visibleNodeIds],
  );

  const linkVisibility = useCallback(
    (link: FGLink) => {
      const src = typeof link.source === "object" ? link.source.id : link.source;
      const tgt = typeof link.target === "object" ? link.target.id : link.target;
      return visibleNodeIds.has(src) && visibleNodeIds.has(tgt);
    },
    [visibleNodeIds],
  );

  // ── Handlers ──

  const handleNodeClick = useCallback(
    (node: FGNode) => onNodeClick(node.id),
    [onNodeClick],
  );

  const toggleType = useCallback((type: string) => {
    setExcludedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, [setExcludedTypes]);

  // ── Empty state ──

  if (graph.nodes.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center gap-4 w-full rounded-lg border border-dashed bg-muted/20 text-muted-foreground ${className ?? "h-[600px]"}`}>
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted ring-1 ring-border">
          <svg className="h-6 w-6 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="5" r="2"/><circle cx="5" cy="19" r="2"/><circle cx="19" cy="19" r="2"/>
            <line x1="12" y1="7" x2="5" y2="17"/><line x1="12" y1="7" x2="19" y2="17"/>
          </svg>
        </div>
        <div className="text-center space-y-1">
          <p className="text-sm font-medium text-foreground/60">No hay grafo para este proyecto</p>
          <p className="text-xs max-w-xs">Conecta un repositorio y ejecuta <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">rai graph build</code> para generar el grafo.</p>
        </div>
      </div>
    );
  }

  const sortedTypes = Object.entries(typeCounts).sort(([, a], [, b]) => b - a);
  const visibleCount = visibleNodeIds.size;

  return (
    <div
      ref={containerRef}
      className={`relative w-full rounded-lg border bg-[#0d1117] overflow-hidden ${className ?? "h-[600px]"}`}
    >
      {/* Filter chips */}
      <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1.5 max-w-[70%]">
        {sortedTypes.map(([type, count]) => (
          <button
            key={type}
            onClick={() => toggleType(type)}
            className={`px-2.5 py-1 rounded-md text-xs cursor-pointer border transition-all select-none ${
              !excludedTypes.has(type)
                ? "border-blue-500/50 bg-blue-500/10 text-blue-400"
                : "border-zinc-700 bg-zinc-900/80 text-zinc-500"
            }`}
          >
            <span style={{ color: NODE_COLORS[type] || DEFAULT_COLOR }}>●</span> {type}{" "}
            <span className="opacity-50">{count}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search nodes..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="absolute top-3 right-3 z-10 px-3 py-1.5 rounded-md border border-zinc-700 bg-zinc-900/80 text-sm text-zinc-300 outline-none focus:border-blue-500 w-48 placeholder:text-zinc-600"
      />

      {/* Graph */}
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        width={dimensions.width}
        height={dimensions.height}
        backgroundColor="#0d1117"
        nodeCanvasObject={nodeCanvasObject}
        nodePointerAreaPaint={nodePointerAreaPaint}
        nodeVisibility={nodeVisibility}
        nodeLabel={getNodeLabel}
        linkColor={getLinkColor}
        linkWidth={getLinkWidth}
        linkCurvature={0}
        linkVisibility={linkVisibility}
        linkDirectionalParticles={isMassive ? 0 : (l: FGLink) => {
          if (!focusSet) return graphData.links.length > 500 ? 0 : 1;
          const src = typeof l.source === "object" ? l.source.id : l.source;
          const tgt = typeof l.target === "object" ? l.target.id : l.target;
          const key = `${src}-${tgt}`;
          const keyRev = `${tgt}-${src}`;
          return focusSet.links.has(key) || focusSet.links.has(keyRev) ? 2 : 0;
        }}
        linkDirectionalParticleWidth={lineWidth * 1.5}
        linkDirectionalParticleSpeed={0.005}
        onNodeClick={handleNodeClick}
        onNodeHover={setHoverNode}
        onEngineStop={() => {
          if (!hasAutoFittedRef.current && fgRef.current) {
            hasAutoFittedRef.current = true;
            fgRef.current.zoomToFit(400, 40);
          }
        }}
        d3VelocityDecay={0.4}
        d3AlphaDecay={0.05}
        cooldownTicks={50}
      />

      {/* Budget indicator */}
      <div className="absolute bottom-3 left-3 z-10 text-xs text-zinc-600">
        {visibleCount} of {graph.total_nodes.toLocaleString()} nodes · {graphData.links.length} edges
      </div>
    </div>
  );
}
